<template>
  <mc-dialog 
    ref="folderSaveDialog"
    class="filter-view-folder-save-dialog"
    :submit-button-text="submitButtonText"
    submit-button-class="primary-submit-button"
    cancel-button-text="Cancel"
    cancel-button-class="primary-cancel-button"
    :close-by-submit-click=false
    @submit="handleSubmitButtonClick"
    @cancel="handleCancelButtonClick">

    <template #header>
      <h3 class="title-dialog">{{ dialogTitle }}</h3>
    </template>

    <section class="body-content">
      <mc-clearable-input ref="folderNameInput" 
        class="filter-input folder-name" 
        :input-value="folderName" 
        label="Folder Name" 
        placeholder="Enter Name..." 
        :clearable="false"
        :error-message="nameErrorMessage"
        @input="validateFolderName($event)"
        @change="handleNameChange($event)"/>
    </section>

  </mc-dialog>
</template>

<script setup lang="ts">
import { nextTick, computed, ref, toRefs } from 'vue';
import _ from 'lodash';
import McClearableInput from '@/components/ui/McClearableInput.vue';
import McDialog from '@/components/ui/McDialog/McDialog.vue';
import { useQuickFilterStore } from '@/store/quickFilterStore';
import { useToastStore } from '@/store/toastStore';
import { 
  FilterViewDialogMode, 
  IQuickFilterFolderOptions,
  IQuickFilterFolderResponse, 
  IQuickFilterFolderViewModel } from '@/models/quickFilter.model';
import { UserActions } from '@/models/appLogger.model';
import { quickFilterDataService } from '@/services/QuickFilterApi';
import { appLogger } from '@/services/AppLogger';

interface IFilterViewFolderSaveDialogProps {
  folder?: IQuickFilterFolderViewModel | undefined;
  dialogMode?: FilterViewDialogMode;
}

const props = withDefaults(defineProps<IFilterViewFolderSaveDialogProps>(), {
  dialogMode: FilterViewDialogMode.create,
});

const { folder, dialogMode } = toRefs(props);

const quickFilterStore = useQuickFilterStore();
const toastStore = useToastStore();

const folderSaveDialog = ref();
const folderNameInput = ref();
const dialogTitle = ref<string>('');
const submitButtonText = ref<string>('');

const folderName = ref<string | undefined>(undefined);
const existingFolders = computed(() => quickFilterStore.quickFilterFolders);

const nameErrorMessage = ref<string | undefined>(undefined);
const nameErrorMessageAlreadyExists = "Folder name already exists, please enter a unique name";  

const emits = defineEmits(["save", "cancel"]);

const openDialog = async () => {
  setDialogAttributes();
  folderSaveDialog.value?.openDialog();
  nextTick(() => folderNameInput.value?.selectValue());
};

const closeDialog = async () => {
  resetToDefaultState();
  folderSaveDialog.value?.closeDialog();
};

const handleSubmitButtonClick = () => {
  validateFolderName(folderName.value);
  if (nameErrorMessage.value) return;

  const requestOptions = {
    id: dialogMode.value === FilterViewDialogMode.edit
      ? folder.value?.id
      : undefined,
    name: folderName.value,
  } as IQuickFilterFolderOptions;

  appLogger.logUserAction(
    dialogMode.value === FilterViewDialogMode.edit
      ? UserActions.viewFolderUpdate
      : UserActions.viewFolderCreate, 
    { folder: requestOptions.name });

  quickFilterDataService.upsertFolderAsync(requestOptions)
    .then((response: IQuickFilterFolderResponse) => {
      if (dialogMode.value === FilterViewDialogMode.edit) {
        quickFilterStore.updateQuickFilterFolder(response);
        toastStore.showItemSuccessfullyRenamed(folder.value?.name ?? "", response.name);
      }
      else {
        quickFilterStore.addQuickFilterFolder(response);
        toastStore.showItemSuccessfullyCreated(response.name, "New folder &nbsp;");
      }
      emits("save", response);
      closeDialog();
    })
    .catch((error: any) => {
      if (error.response?.status === 409) {
        nameErrorMessage.value = nameErrorMessageAlreadyExists;
      }
      else {
        throw error;
      }
    });
};

const handleCancelButtonClick = () => {
  resetToDefaultState();
  emits("cancel");
};

const handleNameChange = (newValue: string | undefined) => {
  validateFolderName(newValue);
  folderName.value = newValue;
};

const validateFolderName = (nameValue: string | undefined) => {
  const nameMaxLength = 40;
  nameValue = nameValue?.trim();

  if (!nameValue) {
    nameErrorMessage.value = "Please enter a name for this Folder";
  } 
  else if (nameValue.length > nameMaxLength) {
    nameErrorMessage.value = `Folder name must not be longer than ${nameMaxLength} characters`;
  }
  else if (existingFolders.value.some((folder) => folder.name.toLowerCase() === nameValue.toLowerCase())) {
    nameErrorMessage.value = nameErrorMessageAlreadyExists;
  }
  else if (!(/^[a-z0-9 ]+$/gi.test(nameValue))){
    nameErrorMessage.value = "Only numbers and letters allowed";
  }
  else {
    nameErrorMessage.value = undefined;
  }
};

const getDefaultFolderName = () : string => {
  const folderNameDefault = "Untitled Folder";
  let foldersWithDefaultNameCount = 0;

  const getDefaultFolderName = (folderName: string) => {
    const existingFolderWithDefaultName = existingFolders.value.find(
      folder => folder.name.toLowerCase() === folderName.toLowerCase());
    
    if (!existingFolderWithDefaultName) {
      return folderName;
    }
    else {
      foldersWithDefaultNameCount++;
      return getDefaultFolderName(`${folderNameDefault} ${foldersWithDefaultNameCount}`);
    }
  };

  return getDefaultFolderName(folderNameDefault);
};

const setDialogAttributes = () => {
  switch (dialogMode.value) {
    case FilterViewDialogMode.create:
      dialogTitle.value = "Create New Folder";
      submitButtonText.value = "Create Folder";
      folderName.value = getDefaultFolderName();
      break;
    case FilterViewDialogMode.edit:
      dialogTitle.value = "Rename Folder";
      submitButtonText.value = "Save";
      folderName.value = _.cloneDeep(folder.value?.name);
      break;
    default:
      submitButtonText.value = "Save";
      break;
  }
};

const resetToDefaultState = () => {
  folderName.value = undefined;
  nameErrorMessage.value = undefined;
};

defineExpose({
  openDialog,
  closeDialog
});
</script>

<style scoped lang="scss">
.filter-view-folder-save-dialog {
  --dialog-width: 427px;
  --dialog-padding: 36px 27px 24px;
  --dialog-content-horizontal-padding: 5px;
  --dialog-actions-section-margin: 0;
  --dialog-action-buttons-padding: 0 8px;

  .title-dialog {
    padding: 0 var(--dialog-content-horizontal-padding);
  }

  .body-content {
    display: flex;
    flex-direction: column;
    padding: 24px var(--dialog-content-horizontal-padding) 32px;
    gap: 24px;

    .folder-name {
      --clearable-input-color: var(--colour-text-neutral-subtle);
    }
  }

  :deep(.primary-submit-button) {
    --button-width: 120px;
  }

  :deep(.primary-cancel-button) {
    --button-width: 100px;
  }
}
</style>